import React, { useEffect, useMemo, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { styled } from '@mui/material/styles';

import RankingInfo from '~/components/Common/RankingInfo';
import { FlexTableCell } from '~/components/Content/SearchRanking/FlexTableCell';
import { Vertical } from '~/components/Content/SearchRanking/SearchRankingPage';
import { Text } from '~/components/Experiences/components';

import { ROLE_SEARCH_CURATOR } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import SearchService, { EvVariant } from '~/services/SearchService';

import { getSearchOrderingRegionCodes } from '~/utils/getSearchOrderingRegionCodes';

import { SearchOrderingScore, getSearchOrderingView, normalizeSearchOrderingScore } from './searchOrderingScore';

interface Props {
  offerId: string;
  vertical: Vertical;
}

const ScoreTable = styled(Table)(() => ({
  '& tr': { height: '100%' },
  '& td': { height: '0' },
  '@supports (-moz-appearance: none)': {
    '& td': { height: '100%' },
  },
}));

const ScoreRegionLine = styled(Box)({
  display: 'flex',
  margin: '8px',
});

const RegionSelector = styled(Select)({
  display: 'inline-block',
  width: '250px',
});

const regionCodes = getSearchOrderingRegionCodes();
const DEFAULT_VARIANT_OPTIONS = {
  [EvVariant.Default]: EvVariant.Default,
};
const SEARCH_CURATOR_ACCESS_MESSAGE =
  'This feature is protected. Contact Sam Drown, Shay Hamama or Fabien Ruffin if you need access';

function getVariantOptionsOrDefault(variantInfos?: Record<string, string>) {
  return variantInfos && Object.keys(variantInfos).length > 0 ? variantInfos : DEFAULT_VARIANT_OPTIONS;
}

export default function SearchOrdering({ offerId, vertical }: Props) {
  const { user } = useCurrentUser();
  const [error, setError] = useState(null);
  const [score, setScore] = useState<SearchOrderingScore | null>(null);
  const [region, setRegion] = useState(regionCodes[0]);
  const [variantOptions, setVariantOptions] = useState<Record<string, string>>(DEFAULT_VARIANT_OPTIONS);
  const [variant, setVariant] = useState<string>(EvVariant.Default);
  const canEditSearchOrdering = user?.roles.includes(ROLE_SEARCH_CURATOR);
  const orderingView = useMemo(() => (score ? getSearchOrderingView(score) : null), [score]);

  useEffect(() => {
    async function fetchScores() {
      if (!offerId) {
        return;
      }

      try {
        const response = await SearchService.getOfferScoreForVariant({
          vertical,
          offerId,
          region,
          variant,
        });
        setScore(normalizeSearchOrderingScore(response.result?.score));
      } catch (error) {
        setError(error);
      }
    }

    fetchScores();
  }, [offerId, variant, region, vertical]);

  useEffect(() => {
    const loadVariants = async () => {
      const { result } = await SearchService.getOfferScoreSettings(vertical, 'AU');
      const variantInfos = getVariantOptionsOrDefault(result?.variants?.variantInfos);
      setVariantOptions(variantInfos);
      setVariant(Object.keys(variantInfos)[0] ?? EvVariant.Default);
    };

    loadVariants();
  }, [vertical]);

  const reload = () => {
    location.reload();
  };

  return (
    <Box>
      <h2 className="page-header">
        Search Ordering
        <Tooltip title={canEditSearchOrdering ? '' : SEARCH_CURATOR_ACCESS_MESSAGE} arrow>
          <span>
            {canEditSearchOrdering ? (
              <Link
                to={{
                  pathname: `/edit-offers/${vertical}/${offerId}/search-orderings`,
                }}
              >
                <IconButton aria-label="Edit" size="small">
                  <EditIcon />
                </IconButton>
              </Link>
            ) : (
              <IconButton aria-label="Edit" size="small" disabled>
                <EditIcon />
              </IconButton>
            )}
          </span>
        </Tooltip>
      </h2>
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" variant="outlined" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error.message}
        </Alert>
      )}
      {!error && (
        <>
          <ScoreRegionLine>
            <Text size="18px" lineHeight="21px" align="left">
              {orderingView?.headline ?? 'Calculated search score: N/A'}
            </Text>
            {orderingView?.kind === 'hotel' && orderingView.info && (
              <Tooltip title={<RankingInfo info={orderingView.info} />} placement="top" arrow>
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            )}
            <Box display="flex" gap={2}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Select an Variant</InputLabel>
                <Select
                  required
                  value={variant}
                  label="Select an Variant"
                  onChange={(event: SelectChangeEvent) => setVariant(event.target.value)}
                  renderValue={(selected) => variantOptions[selected] ?? selected}
                >
                  {Object.entries(variantOptions).map(([option, label]) => (
                    <MenuItem key={option} value={option}>
                      <Radio checked={variant === option} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <RegionSelector
                labelId="offer-search-ordering-region-selector-label"
                id="offer-search-ordering-region-selector"
                defaultValue={region === undefined ? regionCodes[0] : regionCodes.find((code) => code === region)}
                value={region}
                onChange={(event: SelectChangeEvent) => setRegion(event.target.value)}
              >
                {regionCodes.map((regionCode) => {
                  return <MenuItem key={regionCode} value={regionCode}>{`Region(${regionCode})`}</MenuItem>;
                })}
              </RegionSelector>
            </Box>
          </ScoreRegionLine>
          {orderingView?.kind === 'hotel' && (
            <ScoreTable className="offer-search-ordering">
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell align="right">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderingView.rows.map((row) => (
                  <TableRow key={row.label} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {row.label}
                    </TableCell>
                    <TableCell align="right">{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScoreTable>
          )}
          {orderingView?.kind === 'legacy' && (
            <ScoreTable className="offer-search-ordering">
              <TableHead>
                <TableRow>
                  <TableCell> Attribute </TableCell>
                  <TableCell align="right">Raw Value</TableCell>
                  <TableCell align="right">Adjusted Value</TableCell>
                  <TableCell align="right">Weighting</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderingView.rows.map((row) => (
                  <TableRow key={row.attribute} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {row.attribute}
                    </TableCell>
                    <FlexTableCell align="right">
                      {row.value}
                      {row.valueInfo && (
                        <Tooltip title={<RankingInfo info={row.valueInfo} />} placement="top" arrow>
                          <InfoOutlinedIcon fontSize="small" />
                        </Tooltip>
                      )}
                    </FlexTableCell>
                    <FlexTableCell align="right">
                      {row.score}
                      {row.scoreInfo && (
                        <Tooltip title={<RankingInfo info={row.scoreInfo} />} placement="top" arrow>
                          <InfoOutlinedIcon fontSize="small" />
                        </Tooltip>
                      )}
                    </FlexTableCell>
                    <TableCell align="right">{row.weight}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScoreTable>
          )}
        </>
      )}
    </Box>
  );
}
